/**
 * RBAC + ABAC — Permission Surface Constants
 *
 * Mirror of backend src/constant/permissions.constant.ts
 * 55 atomic permission keys split into:
 *   RBAC (24) — global org-level permissions
 *   ABAC (29) — project-scoped permissions
 */

// ──────────────────────────── Projects (RBAC) ─────────────────────
export const P = {
  // ──────────────────────── Clara AI ────────────────────────────────
  CLARA_BRD_ANALYZER: 'module.clara.brd.analyzer',

  PROJECTS_VIEW_ASSIGNED: 'module.projects.view.assigned',
  PROJECTS_VIEW_ALL: 'module.projects.view.all',
  PROJECTS_CREATE: 'module.projects.create',
  PROJECTS_EDIT: 'module.projects.edit',
  PROJECTS_DELETE: 'module.projects.delete',
  /** Archive (freeze) and restore projects. Distinct from edit: archiving hides
   *  and freezes a project for the whole org, so it sits closer to delete. */
  PROJECTS_ARCHIVE: 'module.projects.archive',
  PROJECTS_VIEW_FINANCIALS: 'module.projects.view.financials',
  PROJECTS_VIEW_PROJECT: 'module.projects.view.project',
  PROJECTS_TEAM_ADD: 'module.projects.team.add',
  PROJECTS_TEAM_EDIT: 'module.projects.team.edit',
  PROJECTS_TEAM_REMOVE: 'module.projects.team.remove',
  PROJECTS_VIEW_TEAM: 'module.projects.view.team',
  PROJECTS_VIEW_OVERVIEW: 'module.projects.view.overview',
  PROJECTS_VIEW_APPLY_ALL: 'module.projects.view.apply_all',

  // ──────────────────────── Hierarchy (ABAC — project-scoped) ──────
  HIERARCHY_MODULE_VIEW: 'module.hierarchy.module.view',
  HIERARCHY_MODULE_CREATE: 'module.hierarchy.module.create',
  HIERARCHY_MODULE_EDIT: 'module.hierarchy.module.edit',
  HIERARCHY_MODULE_DELETE: 'module.hierarchy.module.delete',
  HIERARCHY_FEATURE_VIEW: 'module.hierarchy.feature.view',
  HIERARCHY_FEATURE_CREATE: 'module.hierarchy.feature.create',
  HIERARCHY_FEATURE_EDIT: 'module.hierarchy.feature.edit',
  HIERARCHY_FEATURE_DELETE: 'module.hierarchy.feature.delete',
  HIERARCHY_TASK_CREATE: 'module.hierarchy.task.create',
  HIERARCHY_TASK_ASSIGN: 'module.hierarchy.task.assign',
  HIERARCHY_TASK_STATUS: 'module.hierarchy.task.status',
  HIERARCHY_TASK_DELETE: 'module.hierarchy.task.delete',
  HIERARCHY_TASK_DELETE_OWN: 'module.hierarchy.task.delete.own',

  /**
   * Task custom fields. Mirrors projects-api's
   * `src/constant/permissions.constant.ts` — three keys because the acts differ
   * in reach: VIEW sees fields and values, EDIT sets a task's value, MANAGE
   * changes the DEFINITIONS and so the form every project member sees.
   *
   * Hiding a control on VIEW/EDIT is a courtesy; the API is the enforcement
   * layer and 403s a forged request either way.
   */
  TASK_CUSTOM_FIELDS_VIEW: 'module.hierarchy.task.custom_fields.view',
  TASK_CUSTOM_FIELDS_MANAGE: 'module.hierarchy.task.custom_fields.manage',

  // ──────────────────────── Time Tracking ─────────────────────────
  TIME_TRACK_OWN: 'module.time.track.own',
  TIME_TRACK_MANUAL: 'module.time.track.manual',
  TIME_VIEW_TEAM: 'module.time.view.team',
  TIME_VIEW_ALL: 'module.time.view.all',
  TIME_EDIT_TEAM: 'module.time.edit.team',
  TIME_VIEW_RATES: 'module.time.view.rates',

  // ──────────────────────── Timesheets ────────────────────────────
  TIMESHEET_SUBMIT_OWN: 'module.timesheet.submit.own',
  TIMESHEET_VIEW_OWN: 'module.timesheet.view.own',
  TIMESHEET_REVIEW_TEAM: 'module.timesheet.review.team',
  TIMESHEET_REVIEW_PROJECT: 'module.timesheet.review.project',
  TIMESHEET_REVIEW_ALL: 'module.timesheet.review.all',
  TIMESHEET_APPROVE_REJECT: 'module.timesheet.approve',

  // ──────────────────────── Users ─────────────────────────────────
  USERS_VIEW_DIRECTORY: 'module.users.view.directory',
  USERS_VIEW_PROFILE: 'module.users.view.profile',
  USERS_INVITE: 'module.users.invite',
  USERS_EDIT: 'module.users.edit',
  USERS_DEACTIVATE: 'module.users.deactivate',
  USERS_REMOVE: 'module.users.remove',
  USERS_MANAGE_ROLES: 'module.users.manage.roles',

  // ──────────────────────── Settings ──────────────────────────────
  SETTINGS_VIEW: 'module.settings.view',
  SETTINGS_PROJECT_VIEW: 'module.settings.project.view',
  SETTINGS_PROJECT_CONFIG: 'module.settings.project.config',
  SETTINGS_MANAGE_GENERAL: 'module.settings.manage.general',
  SETTINGS_MANAGE_MASTERS: 'module.settings.manage.masters',
  SETTINGS_MANAGE_CONFIG: 'module.settings.manage.config',
  SETTINGS_MY_WORK_MANAGE: 'module.settings.my_work.manage',
  /**
   * Settings → API access. Split in two, because seeing which integrations
   * hold a key is a much smaller thing than being able to mint one:
   *   VIEW   — open the screen, read the key list and the integration guide.
   *   MANAGE — create, rotate and revoke.
   *
   * SCOPE — these gate this UI, not the endpoints. API keys are issued by
   * workspace-api, which cannot read Projects RBAC (cross-app isolation) and
   * enforces the OWNER-HELD `manage_api_keys` workspace capability instead. So
   * granting these widens what a role can SEE, never what it can DO; a holder
   * without workspace authority still gets a 403, which the screen renders as
   * a plain explanation rather than an error.
   */
  SETTINGS_API_KEYS_VIEW: 'module.settings.api_keys.view',
  SETTINGS_API_KEYS_MANAGE: 'module.settings.api_keys.manage',
  /**
   * Project setup templates (task statuses + custom fields). Applying one also
   * needs the project-scoped TASK_CUSTOM_FIELDS_MANAGE, so VIEW alone never
   * lets a role add fields it couldn't add by hand.
   */
  PROJECT_TEMPLATES_VIEW: 'module.settings.project_templates.view',
  PROJECT_TEMPLATES_MANAGE: 'module.settings.project_templates.manage',

  // ──────────────────────── My Work ───────────────────────────
  /**
   * Allows the current user to create / edit / delete their own personal
   * (user-scoped) My Work views and preferences. Independent of
   * SETTINGS_MY_WORK_MANAGE, which controls org-wide defaults.
   */
  MY_WORK_VIEW_MANAGE_PERSONAL: 'module.my_work.view.manage.personal',

  // ──────────────────────── Meetings ──────────────────────────
  MEETINGS_VIEW: 'module.meetings.view',
  MEETINGS_VIEW_ALL: 'module.meetings.view.all',
  MEETINGS_CREATE: 'module.meetings.create',
  MEETINGS_EDIT: 'module.meetings.edit',
  MEETINGS_DELETE: 'module.meetings.delete',
  MEETINGS_CANCEL: 'module.meetings.cancel',
  MEETINGS_CLARA_ENABLE: 'module.meetings.clara.enable',
  MEETINGS_CLARA_INSIGHTS: 'module.meetings.clara.insights',
  MEETINGS_VIEW_MOM: 'module.meetings.view.mom',

  // Spark has NO permission key. Raising one is open to every authenticated
  // user: whoever hits the bug is the person who can describe it. The control is
  // the platform-admin `spark` AppModule, toggled per workspace per app in the
  // console — not anything a workspace admin manages.

  // ──────────────────────── Reports ───────────────────────────
  REPORT_TIMESHEET_VIEW: 'module.report.timesheet.view',
  REPORT_TIMESHEET_EXPORT: 'module.report.timesheet.export',

  // ──────────────────────── My Report (personal, self-scoped) ──
  MY_REPORT_VIEW: 'module.my_report.view',
  MY_REPORT_EXPORT: 'module.my_report.export',

  // ──────────────────────── My Team Report (project-scoped) ────
  // Granted on the projects the role is a MEMBER of: a lead sees task
  // progress for the members of those projects.
  MY_TEAM_REPORT_VIEW: 'module.my_team_report.view',
  MY_TEAM_REPORT_EXPORT: 'module.my_team_report.export',

  // ──────────────────────── Activity / Audit ──────────────────
  ACTIVITY_VIEW_OWN: 'module.activity.view.own',
  ACTIVITY_EXPORT: 'module.activity.export',

  // ──────────────────────── Platform Health ──────────────────
  PLATFORM_HEALTH_VIEW: 'module.platformhealth.view',

  // ──────────────────────── Notifications ───────────────────
  NOTIFICATIONS_CONFIG_MANAGE: 'module.settings.notifications.manage',

  // ──────────────────────── LLM Usage ──────────────────────────
  // Org-wide LLM API spend dashboard (product-side, across providers).
  LLM_USAGE_ADMIN: 'module.llm_usage.admin',
} as const;

/** Union type of all 46 permission surface keys. */
export type PermissionKey = (typeof P)[keyof typeof P];

/** A flat map of permission key → boolean (as stored in JSONB). */
export type PermissionMap = Partial<Record<PermissionKey, boolean>>;

/** All modules for UI display (legacy flat list — used in comparison matrix). */
export const PERMISSION_MODULES = [
  {
    module: 'projects_global',
    label: 'Projects (Org)',
    keys: [
      P.PROJECTS_VIEW_ASSIGNED,
      P.PROJECTS_VIEW_ALL,
      P.PROJECTS_CREATE,
      P.PROJECTS_EDIT,
      P.PROJECTS_ARCHIVE,
      P.PROJECTS_DELETE,
    ],
  },
  {
    module: 'modules_features',
    label: 'Modules & Features',
    keys: [
      P.HIERARCHY_MODULE_VIEW,
      P.HIERARCHY_MODULE_CREATE,
      P.HIERARCHY_MODULE_EDIT,
      P.HIERARCHY_MODULE_DELETE,
      P.HIERARCHY_FEATURE_VIEW,
      P.HIERARCHY_FEATURE_CREATE,
      P.HIERARCHY_FEATURE_EDIT,
      P.HIERARCHY_FEATURE_DELETE,
    ],
  },
  {
    module: 'tasks',
    label: 'Tasks',
    keys: [
      P.HIERARCHY_TASK_CREATE,
      P.HIERARCHY_TASK_ASSIGN,
      P.HIERARCHY_TASK_STATUS,
      P.HIERARCHY_TASK_DELETE,
      P.HIERARCHY_TASK_DELETE_OWN,
      P.TASK_CUSTOM_FIELDS_VIEW,
      P.TASK_CUSTOM_FIELDS_MANAGE,
    ],
  },
  {
    module: 'time',
    label: 'Time Tracking',
    keys: [
      P.TIME_TRACK_OWN,
      P.TIME_TRACK_MANUAL,
      P.TIME_VIEW_TEAM,
      P.TIME_VIEW_ALL,
      P.TIME_EDIT_TEAM,
      P.TIME_VIEW_RATES,
    ],
  },
  {
    module: 'timesheet',
    label: 'Timesheets',
    keys: [
      P.TIMESHEET_SUBMIT_OWN,
      P.TIMESHEET_VIEW_OWN,
      // P.TIMESHEET_REVIEW_TEAM, // temporarily disabled — no team assignment per project yet
      P.TIMESHEET_REVIEW_PROJECT,
      P.TIMESHEET_REVIEW_ALL,
      P.TIMESHEET_APPROVE_REJECT,
    ],
  },
  {
    module: 'users',
    label: 'Users',
    keys: [
      P.USERS_VIEW_DIRECTORY,
      P.USERS_VIEW_PROFILE,
      P.USERS_INVITE,
      P.USERS_EDIT,
      P.USERS_DEACTIVATE,
      P.USERS_REMOVE,
      P.USERS_MANAGE_ROLES,
    ],
  },
  {
    module: 'settings_org',
    label: 'Org Settings',
    keys: [
      P.SETTINGS_VIEW,
      P.SETTINGS_MANAGE_GENERAL,
      P.SETTINGS_MANAGE_MASTERS,
      P.SETTINGS_MANAGE_CONFIG,
      P.SETTINGS_MY_WORK_MANAGE,
      P.SETTINGS_API_KEYS_VIEW,
      P.SETTINGS_API_KEYS_MANAGE,
      P.PROJECT_TEMPLATES_VIEW,
      P.PROJECT_TEMPLATES_MANAGE,
    ],
  },
  {
    module: 'my_work',
    label: 'My Work',
    keys: [P.MY_WORK_VIEW_MANAGE_PERSONAL],
  },
  {
    module: 'project_management',
    label: 'Project Management',
    keys: [
      P.PROJECTS_VIEW_OVERVIEW,
      P.PROJECTS_VIEW_FINANCIALS,
      P.PROJECTS_VIEW_TEAM,
      P.PROJECTS_TEAM_ADD,
      P.PROJECTS_TEAM_EDIT,
      P.PROJECTS_TEAM_REMOVE,
      P.SETTINGS_PROJECT_VIEW,
      P.SETTINGS_PROJECT_CONFIG,
      P.PROJECTS_VIEW_APPLY_ALL,
    ],
  },
  {
    module: 'meetings',
    label: 'Meetings',
    keys: [
      P.MEETINGS_VIEW,
      P.MEETINGS_VIEW_ALL,
      P.MEETINGS_CREATE,
      P.MEETINGS_EDIT,
      P.MEETINGS_DELETE,
      P.MEETINGS_CANCEL,
      P.MEETINGS_CLARA_ENABLE,
      P.MEETINGS_CLARA_INSIGHTS,
      P.MEETINGS_VIEW_MOM,
    ],
  },
  {
    module: 'reports',
    label: 'Reports',
    keys: [P.REPORT_TIMESHEET_VIEW, P.MY_TEAM_REPORT_VIEW, P.MY_TEAM_REPORT_EXPORT],
  },
  {
    module: 'activity',
    label: 'Activity / Audit',
    keys: [P.ACTIVITY_VIEW_OWN, P.ACTIVITY_EXPORT],
  },
  {
    module: 'platformhealth',
    label: 'Platform Health',
    keys: [P.PLATFORM_HEALTH_VIEW],
  },
  {
    module: 'llm_usage',
    label: 'AI Usage',
    keys: [P.LLM_USAGE_ADMIN],
  },
  {
    module: 'clara',
    label: 'Clara AI',
    keys: [P.CLARA_BRD_ANALYZER],
  },
] as const;

// ─── RBAC vs ABAC Grouping (used in TemplateDrawer sectioned UI) ──────────────

/**
 * RBAC modules — global org-level permissions.
 * These are checked without any project context.
 */
export const RBAC_MODULES = [
  {
    module: 'projects_global',
    label: 'Projects (Org-wide)',
    keys: [
      P.PROJECTS_VIEW_ALL,
      P.PROJECTS_CREATE,
      P.PROJECTS_EDIT,
      P.PROJECTS_ARCHIVE,
      P.PROJECTS_DELETE,
    ],
  },
  {
    module: 'users',
    label: 'Users',
    keys: [
      P.USERS_VIEW_DIRECTORY,
      P.USERS_VIEW_PROFILE,
      P.USERS_INVITE,
      P.USERS_EDIT,
      P.USERS_DEACTIVATE,
      P.USERS_REMOVE,
      P.USERS_MANAGE_ROLES,
    ],
  },
  {
    module: 'meetings_global',
    label: 'Meetings (Global)',
    keys: [
      P.MEETINGS_VIEW_ALL,
      P.MEETINGS_CREATE,
      P.MEETINGS_EDIT,
      P.MEETINGS_CANCEL,
      P.MEETINGS_DELETE,
      P.MEETINGS_CLARA_ENABLE,
      P.MEETINGS_CLARA_INSIGHTS,
      P.MEETINGS_VIEW_MOM,
    ],
  },
  {
    module: 'settings_org',
    label: 'Org Settings',
    keys: [
      P.SETTINGS_VIEW,
      P.SETTINGS_MANAGE_GENERAL,
      P.SETTINGS_MANAGE_MASTERS,
      P.SETTINGS_MANAGE_CONFIG,
      P.SETTINGS_MY_WORK_MANAGE,
      P.SETTINGS_API_KEYS_VIEW,
      P.SETTINGS_API_KEYS_MANAGE,
      P.PROJECT_TEMPLATES_VIEW,
      P.PROJECT_TEMPLATES_MANAGE,
    ],
  },
  {
    module: 'my_work',
    label: 'My Work',
    keys: [P.MY_WORK_VIEW_MANAGE_PERSONAL],
  },
  {
    module: 'time_org',
    label: 'Time (Org-wide)',
    keys: [P.TIME_VIEW_ALL, P.TIME_VIEW_RATES, P.TIMESHEET_REVIEW_ALL],
  },
  {
    // Personal, self-scoped reports. The project-scoped timesheet report and
    // My Team Report are registered in `ABAC_MODULES` under `reports_project`.
    module: 'reports',
    label: 'Reports',
    keys: [P.MY_REPORT_VIEW, P.MY_REPORT_EXPORT],
  },
  {
    module: 'activity',
    label: 'Activity / Audit',
    keys: [P.ACTIVITY_VIEW_OWN, P.ACTIVITY_EXPORT],
  },
  {
    module: 'platformhealth',
    label: 'Platform Health',
    keys: [P.PLATFORM_HEALTH_VIEW],
  },
  {
    module: 'llm_usage',
    label: 'AI Usage',
    keys: [P.LLM_USAGE_ADMIN],
  },
] as const;

/**
 * ABAC modules — project-scoped permissions.
 * These are checked inside a specific project context.
 */
export const ABAC_MODULES = [
  {
    module: 'modules_features',
    label: 'Modules & Features',
    keys: [
      P.HIERARCHY_MODULE_VIEW,
      P.HIERARCHY_MODULE_CREATE,
      P.HIERARCHY_MODULE_EDIT,
      P.HIERARCHY_MODULE_DELETE,
      P.HIERARCHY_FEATURE_VIEW,
      P.HIERARCHY_FEATURE_CREATE,
      P.HIERARCHY_FEATURE_EDIT,
      P.HIERARCHY_FEATURE_DELETE,
    ],
  },
  {
    module: 'tasks',
    label: 'Tasks',
    keys: [
      P.HIERARCHY_TASK_CREATE,
      P.HIERARCHY_TASK_ASSIGN,
      P.HIERARCHY_TASK_STATUS,
      P.HIERARCHY_TASK_DELETE,
      P.HIERARCHY_TASK_DELETE_OWN,
      P.TASK_CUSTOM_FIELDS_VIEW,
      P.TASK_CUSTOM_FIELDS_MANAGE,
    ],
  },
  {
    module: 'meetings',
    label: 'Meetings',
    keys: [
      P.MEETINGS_VIEW,
      P.MEETINGS_VIEW_ALL,
      P.MEETINGS_CREATE,
      P.MEETINGS_EDIT,
      P.MEETINGS_DELETE,
      P.MEETINGS_CANCEL,
      P.MEETINGS_CLARA_ENABLE,
      P.MEETINGS_CLARA_INSIGHTS,
      P.MEETINGS_VIEW_MOM,
    ],
  },
  {
    module: 'project_management',
    label: 'Team & Project',
    keys: [
      P.PROJECTS_VIEW_OVERVIEW,
      P.PROJECTS_VIEW_FINANCIALS,
      P.PROJECTS_VIEW_TEAM,
      P.PROJECTS_TEAM_ADD,
      P.PROJECTS_TEAM_EDIT,
      P.PROJECTS_TEAM_REMOVE,
      P.SETTINGS_PROJECT_VIEW,
      P.SETTINGS_PROJECT_CONFIG,
      P.PROJECTS_VIEW_APPLY_ALL,
    ],
  },
  {
    module: 'time_project',
    label: 'Time Tracking',
    keys: [P.TIME_TRACK_OWN, P.TIME_TRACK_MANUAL, P.TIME_VIEW_TEAM, P.TIME_EDIT_TEAM],
  },
  {
    module: 'timesheets_project',
    label: 'Timesheets',
    keys: [
      P.TIMESHEET_SUBMIT_OWN,
      P.TIMESHEET_VIEW_OWN,
      // P.TIMESHEET_REVIEW_TEAM, // temporarily disabled — no team assignment per project yet
      P.TIMESHEET_REVIEW_PROJECT,
      P.TIMESHEET_APPROVE_REJECT,
    ],
  },
  {
    // The code must not collide with an RBAC_MODULES one: the catalog files the
    // first claimant of a code under "Organisation", which would drag these
    // project keys out of the project section. Suffixed like `time_project`.
    module: 'reports_project',
    label: 'Reports',
    // Granting these on a PROJECT template is what scopes each report to that
    // project — the backend resolves the report's project set from these grants.
    keys: [
      P.REPORT_TIMESHEET_VIEW,
      P.REPORT_TIMESHEET_EXPORT,
      P.MY_TEAM_REPORT_VIEW,
      P.MY_TEAM_REPORT_EXPORT,
    ],
  },
  {
    module: 'clara',
    label: 'Clara AI',
    keys: [P.CLARA_BRD_ANALYZER],
  },
] as const;

/**
 * ABAC keys granted only on projects the role is a MEMBER of — the console
 * has no "projects they are NOT a member of" answer for these, so they get
 * no non-member cell (see the projects adapter's `buildDefinitions()`).
 */
export const MEMBER_ONLY_ABAC_KEYS: readonly PermissionKey[] = [
  P.MY_TEAM_REPORT_VIEW,
  P.MY_TEAM_REPORT_EXPORT,
];

/** ABAC workspace groups for comparison matrix and user permission detail views. */
export type AbacMatrixSection = { id: string; label: string; keys: PermissionKey[] };
export type AbacWorkspaceGroup = { id: string; heading: string; rows: AbacMatrixSection[] };

export const ABAC_WORKSPACE_GROUPS: AbacWorkspaceGroup[] = [
  {
    id: 'overview',
    heading: 'Overview',
    rows: [
      { id: 'overview_view', label: 'View project overview', keys: [P.PROJECTS_VIEW_OVERVIEW] },
    ],
  },
  {
    id: 'modules',
    heading: 'Modules',
    rows: [
      { id: 'modules_view', label: 'View modules', keys: [P.HIERARCHY_MODULE_VIEW] },
      {
        id: 'modules',
        label: 'Create, edit & delete modules',
        keys: [P.HIERARCHY_MODULE_CREATE, P.HIERARCHY_MODULE_EDIT, P.HIERARCHY_MODULE_DELETE],
      },
    ],
  },
  {
    id: 'features',
    heading: 'Features',
    rows: [
      { id: 'features_view', label: 'View features', keys: [P.HIERARCHY_FEATURE_VIEW] },
      {
        id: 'features',
        label: 'Create, edit & delete features',
        keys: [P.HIERARCHY_FEATURE_CREATE, P.HIERARCHY_FEATURE_EDIT, P.HIERARCHY_FEATURE_DELETE],
      },
    ],
  },
  {
    id: 'tasks',
    heading: 'Tasks',
    rows: [
      {
        id: 'tasks_view',
        label: 'Change task status & delete own tasks',
        keys: [P.HIERARCHY_TASK_STATUS, P.HIERARCHY_TASK_DELETE_OWN],
      },
      {
        id: 'tasks_manage',
        label: 'Create tasks & delete any task',
        keys: [P.HIERARCHY_TASK_CREATE, P.HIERARCHY_TASK_DELETE],
      },
      { id: 'tasks_assign', label: 'Assign tasks', keys: [P.HIERARCHY_TASK_ASSIGN] },
      {
        id: 'tasks_custom_fields_use',
        label: 'See and fill in task custom fields',
        keys: [P.TASK_CUSTOM_FIELDS_VIEW],
      },
      {
        id: 'tasks_custom_fields_manage',
        label: 'Add, edit & archive task custom fields',
        keys: [P.TASK_CUSTOM_FIELDS_MANAGE],
      },
      {
        id: 'view_apply_all',
        label: 'Apply view for all members',
        keys: [P.PROJECTS_VIEW_APPLY_ALL],
      },
    ],
  },
  {
    id: 'meetings',
    heading: 'Meetings',
    rows: [
      { id: 'meetings_view', label: 'View meetings', keys: [P.MEETINGS_VIEW] },
      {
        id: 'meetings_view_all',
        label: 'View all meetings (including non-participant)',
        keys: [P.MEETINGS_VIEW_ALL],
      },
      {
        id: 'meetings_manage',
        label: 'Create, edit, cancel & delete meetings',
        keys: [P.MEETINGS_CREATE, P.MEETINGS_EDIT, P.MEETINGS_DELETE, P.MEETINGS_CANCEL],
      },
      { id: 'meetings_clara', label: 'Clara AI insights', keys: [P.MEETINGS_CLARA_INSIGHTS] },
      { id: 'meetings_mom', label: 'View meeting MOM', keys: [P.MEETINGS_VIEW_MOM] },
    ],
  },
  {
    id: 'team',
    heading: 'Team',
    rows: [
      { id: 'teams_view', label: 'View project team', keys: [P.PROJECTS_VIEW_TEAM] },
      { id: 'teams_add', label: 'Add team members', keys: [P.PROJECTS_TEAM_ADD] },
      { id: 'teams_edit', label: 'Edit team members', keys: [P.PROJECTS_TEAM_EDIT] },
      { id: 'teams_remove', label: 'Remove team members', keys: [P.PROJECTS_TEAM_REMOVE] },
    ],
  },
  {
    id: 'project_settings',
    heading: 'Project settings',
    rows: [
      {
        id: 'project_settings_view',
        label: 'View project settings',
        keys: [P.SETTINGS_PROJECT_VIEW],
      },
      {
        id: 'project_settings_config',
        label: 'Manage project configuration',
        keys: [P.SETTINGS_PROJECT_CONFIG],
      },
    ],
  },
  {
    id: 'time',
    heading: 'Time tracking',
    rows: [
      {
        id: 'time_track',
        label: 'Track & log own time',
        keys: [P.TIME_TRACK_OWN, P.TIME_TRACK_MANUAL],
      },
      {
        id: 'time_manage',
        label: 'View & edit team time',
        keys: [P.TIME_VIEW_TEAM, P.TIME_EDIT_TEAM],
      },
    ],
  },
  {
    id: 'timesheets',
    heading: 'Timesheets',
    rows: [
      {
        id: 'timesheets_own',
        label: 'Submit & view own timesheet',
        keys: [P.TIMESHEET_SUBMIT_OWN, P.TIMESHEET_VIEW_OWN],
      },
      // { id: 'timesheets_review_team', label: 'Review team timesheets', keys: [P.TIMESHEET_REVIEW_TEAM] }, // temporarily disabled — no team assignment per project yet
      {
        id: 'timesheets_review',
        label: 'Review project timesheets',
        keys: [P.TIMESHEET_REVIEW_PROJECT],
      },
      {
        id: 'timesheets_approve',
        label: 'Approve & reject timesheets',
        keys: [P.TIMESHEET_APPROVE_REJECT],
      },
    ],
  },
  {
    id: 'financials',
    heading: 'Financials',
    rows: [
      { id: 'financials', label: 'View project financials', keys: [P.PROJECTS_VIEW_FINANCIALS] },
    ],
  },
  {
    id: 'reports',
    heading: 'Reports',
    rows: [
      {
        id: 'reports_timesheet',
        label: 'View & export the timesheet report',
        keys: [P.REPORT_TIMESHEET_VIEW, P.REPORT_TIMESHEET_EXPORT],
      },
      {
        id: 'my_team_report_view',
        label: 'View my team report',
        keys: [P.MY_TEAM_REPORT_VIEW],
      },
      {
        id: 'my_team_report_export',
        label: 'Export my team report',
        keys: [P.MY_TEAM_REPORT_EXPORT],
      },
    ],
  },
  {
    id: 'clara',
    heading: 'Clara AI',
    rows: [{ id: 'clara_brd', label: 'BRD Analyzer', keys: [P.CLARA_BRD_ANALYZER] }],
  },
];

/** Human-readable labels for each permission key. */
export const PERMISSION_LABELS: Record<PermissionKey, string> = {
  [P.PROJECTS_VIEW_ASSIGNED]: 'View assigned projects',
  [P.PROJECTS_VIEW_ALL]: 'View all projects',
  [P.PROJECTS_CREATE]: 'Create projects',
  [P.PROJECTS_EDIT]: 'Edit projects',
  [P.PROJECTS_ARCHIVE]: 'Archive & restore projects',
  [P.PROJECTS_DELETE]: 'Delete projects',
  [P.PROJECTS_VIEW_FINANCIALS]: 'View financials',
  [P.PROJECTS_VIEW_PROJECT]: 'View project',
  [P.PROJECTS_TEAM_ADD]: 'Add team members',
  [P.PROJECTS_TEAM_EDIT]: 'Edit team members',
  [P.PROJECTS_TEAM_REMOVE]: 'Remove team members',
  [P.PROJECTS_VIEW_TEAM]: 'View project team',
  [P.PROJECTS_VIEW_OVERVIEW]: 'View project overview',
  [P.PROJECTS_VIEW_APPLY_ALL]: 'Apply view for all members',

  [P.HIERARCHY_MODULE_VIEW]: 'View modules',
  [P.HIERARCHY_MODULE_CREATE]: 'Create modules',
  [P.HIERARCHY_MODULE_EDIT]: 'Edit modules',
  [P.HIERARCHY_MODULE_DELETE]: 'Delete modules',
  [P.HIERARCHY_FEATURE_VIEW]: 'View features',
  [P.HIERARCHY_FEATURE_CREATE]: 'Create features',
  [P.HIERARCHY_FEATURE_EDIT]: 'Edit features',
  [P.HIERARCHY_FEATURE_DELETE]: 'Delete features',
  [P.HIERARCHY_TASK_CREATE]: 'Create tasks',
  [P.HIERARCHY_TASK_ASSIGN]: 'Assign tasks',
  [P.HIERARCHY_TASK_STATUS]: 'Change task status',
  [P.HIERARCHY_TASK_DELETE]: 'Delete any task',
  [P.HIERARCHY_TASK_DELETE_OWN]: 'Delete own tasks',
  [P.TASK_CUSTOM_FIELDS_VIEW]: 'View task custom fields',
  [P.TASK_CUSTOM_FIELDS_MANAGE]: 'Manage task custom fields',

  [P.TIME_TRACK_OWN]: 'Track own time',
  [P.TIME_TRACK_MANUAL]: 'Manual time entry',
  [P.TIME_VIEW_TEAM]: 'View team time',
  [P.TIME_VIEW_ALL]: 'View all time',
  [P.TIME_EDIT_TEAM]: 'Edit team time',
  [P.TIME_VIEW_RATES]: 'View billing rates',

  [P.TIMESHEET_SUBMIT_OWN]: 'Submit own timesheet',
  [P.TIMESHEET_VIEW_OWN]: 'View own timesheet',
  [P.TIMESHEET_REVIEW_TEAM]: 'Review team timesheets',
  [P.TIMESHEET_REVIEW_PROJECT]: 'Review project timesheets',
  [P.TIMESHEET_REVIEW_ALL]: 'Review all timesheets',
  [P.TIMESHEET_APPROVE_REJECT]: 'Approve & reject timesheets',

  [P.USERS_VIEW_DIRECTORY]: 'View user directory',
  [P.USERS_VIEW_PROFILE]: 'View user profiles',
  [P.USERS_INVITE]: 'Invite users',
  [P.USERS_EDIT]: 'Edit users',
  [P.USERS_DEACTIVATE]: 'Deactivate users',
  // Revokes the member's app-access grant — no account is deleted.
  [P.USERS_REMOVE]: 'Revoke user access',
  [P.USERS_MANAGE_ROLES]: 'Manage user roles',

  [P.SETTINGS_VIEW]: 'View settings page',
  [P.SETTINGS_PROJECT_VIEW]: 'View project settings',
  [P.SETTINGS_PROJECT_CONFIG]: 'Manage project configuration',
  [P.SETTINGS_MANAGE_GENERAL]: 'Manage general settings',
  [P.SETTINGS_MANAGE_MASTERS]: 'Manage master data',
  [P.SETTINGS_MANAGE_CONFIG]: 'Manage configuration',
  [P.SETTINGS_MY_WORK_MANAGE]: 'Manage My Work settings',
  [P.SETTINGS_API_KEYS_VIEW]: 'View API access',
  [P.SETTINGS_API_KEYS_MANAGE]: 'Manage API keys',
  [P.PROJECT_TEMPLATES_VIEW]: 'View project templates',
  [P.PROJECT_TEMPLATES_MANAGE]: 'Manage project templates',
  [P.MY_WORK_VIEW_MANAGE_PERSONAL]: 'Manage personal My Work views',

  [P.MEETINGS_VIEW]: 'View meetings',
  [P.MEETINGS_VIEW_ALL]: 'View all meetings',
  [P.MEETINGS_CREATE]: 'Create meetings',
  [P.MEETINGS_EDIT]: 'Edit meetings',
  [P.MEETINGS_DELETE]: 'Delete meetings',
  [P.MEETINGS_CANCEL]: 'Cancel meetings',
  [P.MEETINGS_CLARA_ENABLE]: 'Enable Clara AI',
  [P.MEETINGS_CLARA_INSIGHTS]: 'View Clara AI insights',
  [P.MEETINGS_VIEW_MOM]: 'View meeting MOM',


  [P.REPORT_TIMESHEET_VIEW]: 'View timesheet reports',
  [P.REPORT_TIMESHEET_EXPORT]: 'Export timesheet reports',
  [P.MY_REPORT_VIEW]: 'View my report',
  [P.MY_REPORT_EXPORT]: 'Export my report',
  [P.MY_TEAM_REPORT_VIEW]: 'View my team report',
  [P.MY_TEAM_REPORT_EXPORT]: 'Export my team report',
  [P.ACTIVITY_VIEW_OWN]: 'View own activity',
  [P.ACTIVITY_EXPORT]: 'Export activity',
  [P.PLATFORM_HEALTH_VIEW]: 'View platform health',
  [P.NOTIFICATIONS_CONFIG_MANAGE]: 'Manage notification config',

  [P.LLM_USAGE_ADMIN]: 'Manage LLM usage module',

  [P.CLARA_BRD_ANALYZER]: 'BRD Analyzer (Clara)',
};
